/*
 * Loop Subscriptions "build your own bundle" builder.
 * Spec: docs/superpowers/specs/2026-10-08-bundle-builder-design.md
 *
 * - window.BundleLogic: pure helpers, tested by tests/bundle-builder.test.mjs
 * - <bundle-builder>: sections/bundle-builder.liquid
 * - <bundle-link>: "bundle_link" block of sections/main-product.liquid
 *
 * Both the section and the product block include this file, so it can load twice; the guard keeps one copy.
 */
(() => {
  if (window.BundleLogic) return;

  const LOOP_API = 'https://api.loopsubscriptions.com/storefront/2026-04';
  const SUBSCRIPTION = 'SUBSCRIPTION';
  const ONETIME = 'ONETIME';

  // Liquid renders 123456 cents with the money filter of the current currency ("€1.234,56",
  // "1 234,56 Kč", "1.235 Ft"); separators, decimals and the currency text come from that sample.
  function parseMoneyFormat(sample) {
    const match = String(sample).match(/1(\D?)23(?:4(\D)56|5)/);
    if (!match) return { prefix: '', suffix: '', thousands: ',', decimal: '.' };
    return {
      prefix: sample.slice(0, match.index),
      suffix: sample.slice(match.index + match[0].length),
      thousands: match[1],
      decimal: match[2] || '',
    };
  }

  function formatMoney(cents, format) {
    const [whole, fraction] = (cents / 100).toFixed(format.decimal ? 2 : 0).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands);
    return `${format.prefix}${grouped}${fraction ? format.decimal + fraction : ''}${format.suffix}`;
  }

  const normalize = (text) => String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

  // Translations use [name] placeholders, filled in here.
  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value), template);

  // Loop decides what is in the bundle (products, variants, stock, category); the Shopify data of
  // sections/bundle-builder-data.liquid adds the market's titles, images and prices. Products the
  // market doesn't sell and unavailable variants drop out.
  function mergeProducts(loopBundle, shopData) {
    const products = [];
    for (const loopProduct of loopBundle.products) {
      const shopProduct = shopData.products[String(loopProduct.shopifyId)];
      if (!shopProduct) continue;
      const variants = loopProduct.variants
        .filter((loopVariant) => !loopVariant.outOfStock)
        .map((loopVariant) => ({ id: String(loopVariant.shopifyId), ...shopProduct.variants[String(loopVariant.shopifyId)] }))
        .filter((variant) => variant.available);
      if (!variants.length) continue;
      products.push({ id: String(loopProduct.shopifyId), title: shopProduct.title, categoryId: loopProduct.categoryId, variants });
    }
    return products;
  }

  function indexVariants(products) {
    const index = {};
    for (const product of products) {
      for (const variant of product.variants) index[variant.id] = { ...variant, product };
    }
    return index;
  }

  const purchaseOptions = (bundle) =>
    bundle.purchaseType === 'ALL' ? [SUBSCRIPTION, ONETIME] : [bundle.purchaseType === SUBSCRIPTION ? SUBSCRIPTION : ONETIME];

  const discountsFor = (discounts, purchase) =>
    discounts
      .filter((discount) => discount.purchaseType === 'ALL' || discount.purchaseType === purchase)
      .sort((a, b) => a.minCartQuantity - b.minCartQuantity);

  // Loop tiers are Shopify discount codes with a minimum quantity: the highest one reached / the next one.
  const tierFor = (discounts, quantity, purchase) =>
    discountsFor(discounts, purchase).filter((discount) => discount.minCartQuantity <= quantity).pop() || null;

  const nextTier = (discounts, quantity, purchase) =>
    discountsFor(discounts, purchase).find((discount) => discount.minCartQuantity > quantity) || null;

  // ponytail: only PERCENTAGE tiers are estimated; other Loop discount types still apply at checkout.
  const percentOf = (discount) => (discount && discount.type === 'PERCENTAGE' ? discount.value : 0);

  // "Add 2 more to get 5% off" / "5% off. Add 2 more to save 10%" / "You have the maximum discount of 15%".
  function tierStatus(discounts, count, purchase, strings) {
    const discount = tierFor(discounts, count, purchase);
    const next = nextTier(discounts, count, purchase);
    if (!discount && next) return fill(strings.need_more, { count: next.minCartQuantity - count, percent: percentOf(next) });
    if (discount && next) {
      return fill(strings.tier_next, { percent: percentOf(discount), count: next.minCartQuantity - count, next: percentOf(next) });
    }
    return discount ? fill(strings.tier_max, { percent: percentOf(discount) }) : '';
  }

  // Tier bar items (<ol class="bundle-builder__tiers">) for the builder box and the product page confirmation.
  const tierItems = (discounts, count, purchase, template) =>
    discountsFor(discounts, purchase)
      .map((tier) => `<li class="bundle-builder__tier${tier.minCartQuantity <= count ? ' is-active' : ''}">${escapeHtml(fill(template, { count: tier.minCartQuantity, percent: percentOf(tier) }))}</li>`)
      .join('');

  // Highest tier discount, and the subscription discount of Loop's selling plans (0 without subscriptions).
  const bestTier = (bundle) => Math.max(0, ...bundle.discounts.map(percentOf));
  const planDiscount = (bundle) =>
    purchaseOptions(bundle).includes(SUBSCRIPTION)
      ? Math.max(0, ...bundle.sellingPlans.flatMap((plan) => (plan.discounts || []).map(percentOf)))
      : 0;

  const countItems = (items) => Object.values(items).reduce((sum, quantity) => sum + quantity, 0);

  const unitPrice = (variant, state) =>
    (state.purchase === SUBSCRIPTION ? variant.plans[state.planId] : undefined) ?? variant.price;

  // Estimate for the button; checkout computes the real price. Subscription lines use the plan price
  // from Shopify (plan discount included), the tier percentage comes off the subtotal.
  function totals(variants, state, discount) {
    let regular = 0;
    let subtotal = 0;
    for (const [id, quantity] of Object.entries(state.items)) {
      const variant = variants[id];
      if (!variant) continue;
      regular += variant.price * quantity;
      subtotal += unitPrice(variant, state) * quantity;
    }
    const total = Math.round(subtotal * (1 - percentOf(discount) / 100));
    return { regular, total, savings: regular - total };
  }

  // State: { items: { [variantId]: quantity }, purchase: 'SUBSCRIPTION' | 'ONETIME', planId: string | null }
  function defaultState(bundle) {
    const plan = bundle.sellingPlans[0];
    return { items: {}, purchase: purchaseOptions(bundle)[0], planId: plan ? String(plan.shopifyId) : null };
  }

  // Keeps what is still valid in a saved or edited state; unknown variants and plans fall back.
  function sanitizeState(state, variants, bundle) {
    const fallback = defaultState(bundle);
    const items = {};
    for (const [id, quantity] of Object.entries((state && state.items) || {})) {
      if (variants[id] && quantity > 0) items[id] = Math.floor(quantity);
    }
    const purchase = state && purchaseOptions(bundle).includes(state.purchase) ? state.purchase : fallback.purchase;
    const planKnown = state && bundle.sellingPlans.some((plan) => String(plan.shopifyId) === String(state.planId));
    return { items, purchase, planId: planKnown ? String(state.planId) : fallback.planId };
  }

  function setQuantity(state, variantId, quantity) {
    const items = { ...state.items };
    if (quantity > 0) items[variantId] = quantity;
    else delete items[variantId];
    return { ...state, items };
  }

  // In-stock variants of the Loop bundle, as a lookup for sanitizeState/addVariant on the product page.
  const bundleVariants = (bundle) =>
    Object.fromEntries(bundle.products.flatMap((product) =>
      product.variants.filter((variant) => !variant.outOfStock).map((variant) => [String(variant.shopifyId), true])));

  // ?add=<variantId> from the product page link; ignored when the variant isn't in the bundle.
  const addVariant = (state, variantId, variants) =>
    variants[variantId] ? setQuantity(state, variantId, (state.items[variantId] || 0) + 1) : state;

  // Lines of one bundle from /cart.js -> builder state, for "Edit" in the cart.
  function stateFromCart(cartItems, txnId) {
    const lines = cartItems.filter((item) => item.properties && item.properties._bundleId === txnId);
    if (!lines.length) return null;
    const items = {};
    for (const line of lines) items[String(line.variant_id)] = (items[String(line.variant_id)] || 0) + line.quantity;
    const allocation = lines[0].selling_plan_allocation;
    return {
      state: { items, purchase: allocation ? SUBSCRIPTION : ONETIME, planId: allocation ? String(allocation.selling_plan.id) : null },
      lines,
    };
  }

  // Loop "Create transaction" body. quantity counts bundles and must stay 1; item counts go to productVariants.
  function transactionPayload(bundle, state, discount) {
    return {
      boxSizeId: bundle.boxSizes[0].id,
      sellingPlanShopifyId: state.purchase === SUBSCRIPTION ? Number(state.planId) : null,
      ...(discount ? { discountId: discount.id } : {}),
      quantity: 1,
      productVariants: Object.entries(state.items).map(([id, quantity]) => ({ shopifyId: Number(id), quantity })),
    };
  }

  // /cart/add.js items. Loop links the lines to the transaction through _bundleId (exact key).
  function cartLines(state, txnId, bundleName) {
    return Object.entries(state.items).map(([id, quantity]) => ({
      id: Number(id),
      quantity,
      ...(state.purchase === SUBSCRIPTION ? { selling_plan: Number(state.planId) } : {}),
      properties: { _bundleId: txnId, _bundleName: bundleName },
    }));
  }

  // Web storage can be missing or throw (private mode, blocked site data); the builder then just forgets.
  function readJson(storageName, key) {
    try {
      return JSON.parse(window[storageName].getItem(key));
    } catch (error) {
      return null;
    }
  }

  function writeJson(storageName, key, value) {
    try {
      window[storageName].setItem(key, JSON.stringify(value));
    } catch (error) {
      // not persisted
    }
  }

  window.BundleLogic = {
    parseMoneyFormat, formatMoney, normalize, escapeHtml, fill, mergeProducts, indexVariants, purchaseOptions,
    tierFor, nextTier, tierStatus, tierItems, bestTier, planDiscount, percentOf, countItems, unitPrice, totals, defaultState, sanitizeState, setQuantity,
    bundleVariants, addVariant, stateFromCart, transactionPayload, cartLines, readJson, writeJson,
  };

  if (!window.customElements) return;

  // Requests time out so a hanging API ends in the error state instead of an endless "Loading".
  const TIMEOUT = 15000;
  // Safari < 16 has no AbortSignal.timeout; those requests then just run without the limit.
  const timeout = () => (AbortSignal.timeout ? AbortSignal.timeout(TIMEOUT) : undefined);

  async function loadLoopBundle(bundleId) {
    const key = `bundle-builder:loop:${bundleId}`;
    const cached = readJson('sessionStorage', key);
    if (cached) return cached;
    const response = await fetch(`${LOOP_API}/bundle/${encodeURIComponent(bundleId)}`, { signal: timeout() });
    const json = await response.json();
    if (!response.ok || !json.success) throw new Error(`Loop bundle: ${json.message || response.status}`);
    writeJson('sessionStorage', key, json.data);
    return json.data;
  }

  async function loadShopData() {
    const response = await fetch(`${window.Shopify.routes.root}?section_id=bundle-builder-data`, { signal: timeout() });
    if (!response.ok) throw new Error(`bundle-builder-data: ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = JSON.parse(html.querySelector('[data-bundle-data]').textContent);
    // Plan names are per product; one lookup is enough.
    data.planNames = Object.assign({}, ...Object.values(data.products).map((product) => product.planNames));
    data.moneySample = html.querySelector('[data-money-sample]').textContent.trim();
    return data;
  }

  async function createTransaction(bundle, payload) {
    const response = await fetch(`${LOOP_API}/bundle/${encodeURIComponent(bundle.id)}/transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: timeout(),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || !json.success || !json.data || !json.data.txnId) {
      throw new Error(`Loop transaction: ${json.message || response.status}`);
    }
    return json.data.txnId;
  }

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.config = window.bundleConfig || {};
      this.strings = Object.fromEntries(
        [...this.querySelectorAll('[data-strings] [data-key]')].map((element) => [element.dataset.key, element.textContent]),
      );
      this.icons = {
        plus: this.querySelector('template[data-icon="plus"]').innerHTML,
        minus: this.querySelector('template[data-icon="minus"]').innerHTML,
      };
      this.picker = this.querySelector('[data-picker]');
      this.search = this.querySelector('[data-search]');
      this.category = '';
      this.chosenVariant = {};
      this.edit = null;
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.search.addEventListener('input', () => this.renderResults());
      this.picker.addEventListener('click', (event) => {
        if (event.target === this.picker) this.picker.close(); // click on the backdrop
      });

      if (!this.config.bundleId) {
        this.showError(this.strings.load_error);
        return;
      }
      new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.load();
      }, { rootMargin: '600px 0px' }).observe(this);
    }

    storageKey() {
      return `bundle-builder:${this.config.bundleId}`;
    }

    async load() {
      this.showError('');
      try {
        const [bundle, shopData] = await Promise.all([loadLoopBundle(this.config.bundleId), loadShopData()]);
        this.bundle = bundle;
        this.money = parseMoneyFormat(shopData.moneySample);
        this.planNames = shopData.planNames;
        this.products = mergeProducts(bundle, shopData);
        this.variants = indexVariants(this.products);
        this.state = sanitizeState(await this.initialState(), this.variants, bundle);
      } catch (error) {
        console.error(error);
        this.showError(this.strings.load_error, true);
        return;
      }
      this.renderOptions();
      this.render();
      this.querySelector('[data-action="open"]').disabled = false;
      if (this.notice || this.edit) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Cart lines for ?edit=<txnId>; otherwise the saved selection plus ?add=<variantId> from a product page.
    async initialState() {
      const params = new URLSearchParams(window.location.search);
      const editId = params.get('edit');
      if (editId) {
        const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((response) => response.json());
        const edit = stateFromCart(cart.items, editId);
        if (edit) {
          this.edit = { txnId: editId, lines: edit.lines };
          return edit.state;
        }
      }
      let state = sanitizeState(readJson('localStorage', this.storageKey()), this.variants, this.bundle);
      const addId = params.get('add');
      if (addId) {
        if (this.variants[addId]) this.notice = this.strings.added;
        state = addVariant(state, addId, this.variants);
        params.delete('add');
        const query = params.toString();
        history.replaceState(history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      }
      return state;
    }

    // The product page may change the saved selection while this builder is loaded (its "Added" dialog).
    refresh() {
      if (!this.bundle || this.edit) return;
      this.state = sanitizeState(readJson('localStorage', this.storageKey()), this.variants, this.bundle);
      this.renderOptions();
      this.render();
    }

    renderOptions() {
      const purchase = purchaseOptions(this.bundle);
      this.querySelector('[data-purchase]').hidden = purchase.length < 2;
      this.querySelectorAll('[data-purchase-input]').forEach((input) => {
        input.checked = input.value === this.state.purchase;
      });

      const select = this.querySelector('[data-plan]');
      select.innerHTML = this.bundle.sellingPlans
        .map((plan) => `<option value="${escapeHtml(plan.shopifyId)}">${escapeHtml(this.planNames[plan.shopifyId] || plan.name)}</option>`)
        .join('');
      select.value = this.state.planId;

      // Benefits under the intro: percentages from Loop; no subscription line without a subscription discount.
      const benefits = this.querySelector('[data-benefits]');
      if (benefits) {
        for (const [name, percent] of [['tiers', bestTier(this.bundle)], ['subscription', planDiscount(this.bundle)]]) {
          const item = benefits.querySelector(`[data-benefit="${name}"]`);
          const text = item.querySelector('span');
          text.dataset.template = text.dataset.template || text.textContent;
          text.textContent = fill(text.dataset.template, { percent });
          item.hidden = !percent;
        }
        benefits.hidden = false;
      }

      if (!this.config.categories) return;
      const used = new Set(this.products.map((product) => product.categoryId));
      const categories = this.bundle.categories.filter((category) => used.has(category.id));
      const chips = this.querySelector('[data-chips]');
      chips.hidden = categories.length < 2;
      chips.innerHTML = [{ id: '', title: this.strings.all }, ...categories]
        .map((category) => `<button type="button" class="bundle-builder__chip" data-action="category" data-category="${escapeHtml(category.id)}" aria-pressed="${category.id === this.category}">${escapeHtml(category.title)}</button>`)
        .join('');
    }

    render() {
      const { strings, state } = this;
      const count = countItems(state.items);
      const discount = tierFor(this.bundle.discounts, count, state.purchase);
      const box = this.bundle.boxSizes[0];
      this.canAdd = !box.maxSize || count < box.maxSize;
      const status = tierStatus(this.bundle.discounts, count, state.purchase, strings);

      this.querySelector('[data-title]').textContent = fill(strings.box_title, { count });
      this.querySelector('[data-status]').textContent = [this.notice, status].filter(Boolean).join(' ');
      this.querySelector('[data-picker-status]').innerHTML =
        `<strong>${escapeHtml(fill(strings.selected, { count }))}</strong><br>${escapeHtml(status)}`;
      this.querySelector('[data-tiers]').innerHTML = tierItems(this.bundle.discounts, count, state.purchase, strings.tier);

      const lines = Object.entries(state.items);
      this.querySelector('[data-items]').innerHTML = lines.length
        ? lines.map(([id, quantity]) => this.itemRow(this.variants[id], quantity)).join('')
        : `<li class="bundle-builder__empty">${escapeHtml(strings.empty)}</li>`;

      this.querySelector('[data-plan-wrapper]').hidden = state.purchase !== SUBSCRIPTION;
      this.renderSubmit(count, box.minSize, discount);
      if (this.picker.open) this.renderResults();
      if (!this.edit) writeJson('localStorage', this.storageKey(), state);
    }

    renderSubmit(count, min, discount) {
      const { strings } = this;
      const button = this.querySelector('[data-action="submit"]');
      const label = this.querySelector('[data-submit-label]');
      const savings = this.querySelector('[data-submit-savings]');
      if (count < min) {
        button.disabled = true;
        label.textContent = fill(strings.min_items, { count: min });
        savings.textContent = '';
        return;
      }
      const result = totals(this.variants, this.state, discount);
      button.disabled = Boolean(this.busy);
      label.textContent = fill(this.edit ? strings.save_changes : strings.add_to_cart, { price: formatMoney(result.total, this.money) });
      savings.innerHTML = result.savings > 0
        ? `<s>${escapeHtml(formatMoney(result.regular, this.money))}</s> ${escapeHtml(fill(strings.savings, { amount: formatMoney(result.savings, this.money) }))}`
        : '';
    }

    renderResults() {
      const query = normalize(this.search.value.trim());
      const products = this.products.filter((product) =>
        (!this.category || product.categoryId === this.category) && (!query || normalize(product.title).includes(query)));
      this.querySelector('[data-results]').innerHTML = products.map((product) => this.resultRow(product)).join('');
      this.querySelector('[data-no-results]').hidden = products.length > 0;
    }

    image(variant) {
      return variant.image ? `<img src="${escapeHtml(variant.image)}" alt="" width="56" height="56" loading="lazy">` : '';
    }

    itemRow(variant, quantity) {
      const { product } = variant;
      const meta = [product.variants.length > 1 ? variant.title : '', formatMoney(unitPrice(variant, this.state) * quantity, this.money)]
        .filter(Boolean)
        .join(' · ');
      return `<li class="bundle-builder__row">
        ${this.image(variant)}
        <div class="bundle-builder__row-info">
          <span class="bundle-builder__row-title">${escapeHtml(product.title)}</span>
          <span class="bundle-builder__row-meta">${escapeHtml(meta)}</span>
        </div>
        ${this.quantityControl(variant, quantity)}
      </li>`;
    }

    resultRow(product) {
      const chosen = this.chosenVariant[product.id]
        || (product.variants.find((variant) => this.state.items[variant.id]) || product.variants[0]).id;
      const variant = this.variants[chosen];
      const options = product.variants.length > 1
        ? `<span class="bundle-builder__variants">${product.variants
          .map((option) => `<button type="button" class="bundle-builder__variant" data-action="variant" data-product="${product.id}" data-variant="${option.id}" aria-pressed="${option.id === variant.id}">${escapeHtml(option.title)}</button>`)
          .join('')}</span>`
        : '';
      return `<li class="bundle-builder__row">
        ${this.image(variant)}
        <div class="bundle-builder__row-info">
          <span class="bundle-builder__row-title">${escapeHtml(product.title)}</span>
          ${options}
          <span class="bundle-builder__row-meta">${escapeHtml(formatMoney(unitPrice(variant, this.state), this.money))}</span>
        </div>
        ${this.quantityControl(variant, this.state.items[variant.id] || 0)}
      </li>`;
    }

    quantityControl(variant, quantity) {
      const { strings } = this;
      const title = variant.product.title;
      const add = `<button type="button" class="bundle-builder__qty-button${quantity ? '' : ' bundle-builder__qty-button--add'}" data-action="add" data-variant="${variant.id}" aria-label="${escapeHtml(fill(quantity ? strings.increase : strings.add_item, { product: title }))}"${this.canAdd ? '' : ' disabled'}>${this.icons.plus}</button>`;
      if (!quantity) return add;
      return `<div class="bundle-builder__stepper">
        <button type="button" class="bundle-builder__qty-button" data-action="remove" data-variant="${variant.id}" aria-label="${escapeHtml(fill(strings.decrease, { product: title }))}">${this.icons.minus}</button>
        <output>${quantity}</output>
        ${add}
      </div>`;
    }

    onClick(event) {
      const target = event.target.closest('[data-action]');
      if (!target || !this.contains(target) || target.disabled) return;
      const { action, variant, product, category } = target.dataset;

      if (action === 'open') {
        this.renderResults();
        this.picker.showModal();
        if (window.matchMedia('(min-width: 750px)').matches) this.search.focus();
      } else if (action === 'close') {
        this.picker.close();
      } else if (action === 'retry') {
        this.load();
      } else if (action === 'category') {
        this.category = category;
        this.querySelectorAll('[data-action="category"]').forEach((chip) => {
          chip.setAttribute('aria-pressed', String(chip.dataset.category === category));
        });
        this.renderResults();
      } else if (action === 'variant') {
        this.chosenVariant[product] = variant;
        this.refocus(target, () => this.renderResults());
      } else if (action === 'add' || action === 'remove') {
        this.notice = '';
        this.state = setQuantity(this.state, variant, (this.state.items[variant] || 0) + (action === 'add' ? 1 : -1));
        this.refocus(target, () => this.render());
      } else if (action === 'submit') {
        this.submit();
      }
    }

    onChange(event) {
      if (event.target.matches('[data-purchase-input]')) this.state = { ...this.state, purchase: event.target.value };
      else if (event.target.matches('[data-plan]')) this.state = { ...this.state, planId: event.target.value };
      else return;
      this.render();
    }

    // Re-rendering replaces the buttons; keep keyboard focus on the same control, or the row's "+",
    // or "Add a product" when the row is gone.
    refocus(target, update) {
      const list = target.closest('[data-items], [data-results]');
      const { action, variant } = target.dataset;
      update();
      if (!list) return;
      const next = list.querySelector(`[data-action="${action}"][data-variant="${variant}"]`)
        || list.querySelector(`[data-action="add"][data-variant="${variant}"]`)
        || this.querySelector('[data-action="open"]');
      next.focus();
    }

    showError(message, retry = false) {
      const error = this.querySelector('[data-error]');
      error.hidden = !message;
      error.innerHTML = message
        ? `${escapeHtml(message)}${retry ? ` <button type="button" class="link" data-action="retry">${escapeHtml(this.strings.retry)}</button>` : ''}`
        : '';
    }
    // New transaction -> bundle discount code -> lines -> (edit) old lines out. The new lines go in first,
    // so a rejected add (sold out) or a lost connection leaves the old bundle in the cart untouched.
    // ponytail: a variant with stock for only one bundle at a time is rejected while both are in the cart;
    // the shopper then removes the bundle in the cart and builds it again.
    async submit() {
      if (this.busy) return;
      const discount = tierFor(this.bundle.discounts, countItems(this.state.items), this.state.purchase);
      const root = window.Shopify.routes.root;
      this.setBusy(true);
      this.showError('');
      try {
        const txnId = await createTransaction(this.bundle, transactionPayload(this.bundle, this.state, discount));
        // The code goes first: cart-bundle.js re-renders and opens the cart drawer after every /cart/add
        // carrying _bundleId, and that render should already show the bundle discount.
        if (discount) await fetch(`${root}discount/${encodeURIComponent(discount.code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`);
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          ...fetchConfig(),
          body: JSON.stringify({ items: cartLines(this.state, txnId, this.bundle.name) }),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw Object.assign(new Error('cart add failed'), { userMessage: error.description });
        }
        if (this.edit) {
          // The new bundle is in; if the old lines can't be removed now, both stay visible in the cart.
          await this.removeLines(this.edit.lines).catch((error) => console.error(error));
          // cart-bundle.js rendered the drawer right after the add, still with the old lines.
          if (window.renderCartDrawer) await window.renderCartDrawer(false);
        }
        this.finish();
      } catch (error) {
        console.error(error);
        this.showError(error.userMessage || this.strings.add_error);
      } finally {
        this.setBusy(false);
      }
    }

    removeLines(lines) {
      return fetch(`${window.routes.cart_update_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify({ updates: Object.fromEntries(lines.map((line) => [line.key, 0])) }),
      });
    }

    finish() {
      const wasEditing = Boolean(this.edit);
      if (wasEditing) {
        this.edit = null;
        const url = new URL(window.location.href);
        url.searchParams.delete('edit');
        history.replaceState(history.state, '', url);
      }
      // After an edit, go back to the visitor's saved (unrelated) selection; after a new bundle, start empty.
      this.state = wasEditing
        ? sanitizeState(readJson('localStorage', this.storageKey()), this.variants, this.bundle)
        : { ...this.state, items: {} };
      this.notice = '';
      if (this.picker.open) this.picker.close();
      this.render();
      this.dispatchEvent(new CustomEvent('bundle-builder:added', { bubbles: true }));
      // With the drawer, cart-bundle.js opens it after the add; without it, show the cart page.
      if (!document.querySelector('cart-drawer')) window.location.href = window.routes.cart_url;
    }

    setBusy(busy) {
      this.busy = busy;
      const button = this.querySelector('[data-action="submit"]');
      button.classList.toggle('loading', busy);
      button.querySelector('.loading-overlay-spinner').classList.toggle('hidden', !busy);
      // The submit reads the selection across several requests; it must not change meanwhile.
      this.querySelector('.bundle-builder__box').inert = busy;
      if (busy) button.disabled = true;
      else this.render();
    }
  }

  customElements.define('bundle-builder', BundleBuilder);

  // Product page, for variants in the market's Loop bundle: the line "Save up to X% in a bundle - Build a
  // bundle with this product" (opens the builder with ?add=), or, once the visitor has a bundle in progress
  // in the builder (its saved selection), an "Add to bundle" button that adds the variant to it right here.
  class BundleLink extends HTMLElement {
    async connectedCallback() {
      const { bundleId, pageUrl } = window.bundleConfig || {};
      if (!bundleId || !pageUrl) return;
      try {
        this.bundle = await loadLoopBundle(bundleId);
      } catch (error) {
        console.error(error);
        return;
      }
      if (!this.bundle.products.some((product) => String(product.shopifyId) === this.dataset.productId)) return;

      this.storageKey = `bundle-builder:${bundleId}`;
      this.pageUrl = pageUrl;
      this.inBundle = bundleVariants(this.bundle);
      this.variantId = this.dataset.variantId;
      this.strings = Object.fromEntries(
        [...this.querySelectorAll(':scope > [data-strings] [data-key]')].map((element) => [element.dataset.key, element.textContent]),
      );
      const best = bestTier(this.bundle);
      const save = this.querySelector('[data-save]');
      save.textContent = fill(save.textContent, { percent: best });
      save.hidden = !best;
      this.querySelector('[data-finish]').href = pageUrl;
      this.querySelector('[data-add]').addEventListener('click', () => this.add());
      // "Added to your bundle" holds a full builder: changes there show here once it closes, and a bundle
      // added to the cart from it closes it so the cart drawer is visible.
      this.dialog = this.querySelector('[data-added]');
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-close]')) this.dialog.close(); // backdrop or buttons
      });
      this.dialog.addEventListener('close', () => this.update());
      this.dialog.addEventListener('bundle-builder:added', () => this.dialog.close());
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.dataset.sectionId || !data.variant) return;
        if (!this.dataset.variantIds.split(',').includes(String(data.variant.id))) return; // another product
        this.variantId = String(data.variant.id);
        const variantTitle = this.dialog.querySelector('[data-added-variant]');
        if (variantTitle) variantTitle.textContent = data.variant.title;
        this.update();
      });
      // Coming back from the builder (back/forward cache), the saved bundle may have changed.
      this.onPageShow = () => this.update();
      window.addEventListener('pageshow', this.onPageShow);
      this.update();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      window.removeEventListener('pageshow', this.onPageShow);
    }

    // The builder's saved selection, without variants that left the bundle or sold out.
    draft() {
      return sanitizeState(readJson('localStorage', this.storageKey), this.inBundle, this.bundle);
    }

    add() {
      writeJson('localStorage', this.storageKey, addVariant(this.draft(), this.variantId, this.inBundle));
      this.update();
      this.showAdded();
    }

    status(draft, count) {
      return tierStatus(this.bundle.discounts, count, draft.purchase, this.strings);
    }

    // Confirmation with the bundle builder in it, so the bundle can go to the cart right from here. Its
    // builder loads when the dialog first shows; later it re-reads the selection this page just saved.
    showAdded() {
      this.dialog.showModal();
      this.dialog.querySelector('bundle-builder').refresh();
    }

    update() {
      this.hidden = !this.inBundle[this.variantId];
      const draft = this.draft();
      const count = countItems(draft.items);
      const box = this.bundle.boxSizes[0];
      this.querySelector('[data-intro]').hidden = count > 0;
      this.querySelector('[data-draft]').hidden = count === 0;
      this.querySelector('[data-link]').href = `${this.pageUrl}?add=${encodeURIComponent(this.variantId)}`;
      const quantity = draft.items[this.variantId] || 0;
      const add = this.querySelector('[data-add]');
      add.disabled = Boolean(box.maxSize) && count >= box.maxSize;
      add.classList.toggle('bundle-link__add--in', quantity > 0);
      this.addLabel = this.addLabel || add.querySelector('[data-add-label]').textContent;
      add.querySelector('[data-add-label]').textContent = quantity ? fill(this.strings.in_bundle, { count: quantity }) : this.addLabel;
      this.querySelector(':scope > [data-draft] [data-status]').textContent = [fill(this.strings.box_title, { count }), this.status(draft, count)]
        .filter(Boolean)
        .join(' · ');
    }
  }

  customElements.define('bundle-link', BundleLink);
})();
